import { useMediaQuery, useTheme } from '@mui/material';
import type {
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import { useLink } from '~/hooks/link';
import { useOrderByParams } from '~/hooks/order-by';
import CreateShowIllustration from '~/images/illustrations/create-show.svg';
import type {
  ShowList__ShowFolder as Folder,
  ShowList__Show as Show,
} from '../../queries/queries.generated';
import { useColumns } from '../lib';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={CreateShowIllustration}
      header={t('pages.shows.components.empty_state.header')}
      description={t('pages.shows.components.empty_state.description')}
    />
  );
};

export interface TableProps {
  loading: boolean;
  shows: readonly Show[];
  folders: ReadonlyArray<Folder>;
  selectedShow?: Show;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  onSelectedShow: (show: Show | undefined) => void;
  selectedIds: GridRowSelectionModel;
  search?: boolean;
}

type ShowOrFolder = Show | Folder;

export const Table = ({
  loading,
  shows,
  folders,
  selectedShow,
  onCheck,
  onSelectedShow,
  selectedIds,
  search = false,
}: TableProps) => {
  const columns = useColumns({ selectedShow, onSelectedShow, search });
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});
  const link = useLink();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  searchParams.set('page', '1');
  searchParams.delete('viewId');

  const combinedData = [...folders, ...shows];

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumAndDown = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeAndDown = useMediaQuery(theme.breakpoints.down('lg'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      deviceGroups: !isMediumAndDown,
      id: !isSmallAndDown,
      location: !isLargeAndDown,
      showCount: !search && !isSmallAndDown,
      viewIds: !isLargeAndDown,
    }));
  }, [isLargeAndDown, isMediumAndDown, isSmallAndDown, search]);

  return (
    <ScrollableDataGrid
      checkboxSelection={!isSmallAndDown}
      columns={columns}
      columnVisibilityModel={showColumns}
      disableRowSelectionOnClick
      isRowSelectable={({ row }) => row.__typename !== 'ShowFolder'}
      loading={loading}
      onColumnVisibilityModelChange={setShowColumns}
      onRowSelectionModelChange={onCheck}
      onRowClick={({ row }: GridRowParams<ShowOrFolder>) => {
        if (row.__typename === 'ShowFolder') {
          navigate({ ...link(`/shows/${row.id}`), search: searchParams.toString() });
          return;
        }
        navigate({
          ...link(`/shows/${row.showFolderId}/show/${row.id}/design`),
          search: searchParams.toString(),
        });
      }}
      getRowId={(row) => (row.__typename === 'Show' ? row.id : `ShowFolder-${row.id}`)}
      rows={combinedData}
      rowSelectionModel={selectedIds}
      slots={{
        baseCheckbox: ListCheckbox,
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
};
